import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../Common/Layout/AdminHeader';
import { rexTechTheme } from '../Common/Theme';
import { 
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Shield,
  Save
} from 'lucide-react';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password validation rules
  const passwordRules = [
    { label: 'At least 8 characters', regex: /.{8,}/ },
    { label: 'At least one uppercase letter', regex: /[A-Z]/ },
    { label: 'At least one lowercase letter', regex: /[a-z]/ },
    { label: 'At least one number', regex: /[0-9]/ },
    { label: 'At least one special character', regex: /[!@#$%^&*(),.?":{}|<>]/ }
  ];

  // Check password against rules
  const checkPasswordRules = (password) => {
    return passwordRules.map(rule => ({
      ...rule,
      satisfied: rule.regex.test(password)
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Current password validation
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    } else if (formData.currentPassword.length < 8) {
      newErrors.currentPassword = 'Current password must be at least 8 characters';
      isValid = false;
    }

    // New password validation
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else {
      const rules = checkPasswordRules(formData.newPassword);
      const unsatisfiedRules = rules.filter(rule => !rule.satisfied);
      if (unsatisfiedRules.length > 0) {
        newErrors.newPassword = 'Password does not meet all requirements';
        isValid = false;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // Check if new password is same as current
    if (formData.newPassword && formData.currentPassword && 
        formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'New password must be different from current password';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Here you would make your actual API call
      // Example:
      // const response = await axios.post('/api/change-password', {
      //   currentPassword: formData.currentPassword,
      //   newPassword: formData.newPassword
      // });
      
      // For demo purposes, simulate success
      setSuccessMessage('Password changed successfully!');
      
      // Reset form on success
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessage(
        error.response?.data?.message || 
        'Failed to change password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Password input component
  const PasswordInput = ({ 
    label, 
    name, 
    value, 
    error, 
    showPassword, 
    onToggle, 
    onChange,
    placeholder 
  }) => (
    <div>
      <label style={{
        display: 'block',
        marginBottom: rexTechTheme.spacing.xs,
        color: rexTechTheme.colors.primary.dark,
        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
        fontSize: rexTechTheme.typography.fontSize.regular
      }}>
        {label}
        <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md}`,
            border: `2px solid ${error ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
            borderRadius: rexTechTheme.borderRadius.medium,
            fontSize: rexTechTheme.typography.fontSize.regular,
            transition: 'all 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box',
            ':focus': {
              borderColor: rexTechTheme.colors.primary.main,
              boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
            }
          }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position: 'absolute',
            right: rexTechTheme.spacing.md,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: rexTechTheme.colors.neutral.darkGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xs,
            ':hover': {
              color: rexTechTheme.colors.primary.main
            }
          }}
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: rexTechTheme.spacing.xs,
          marginTop: rexTechTheme.spacing.xs,
          color: rexTechTheme.colors.accent.orange,
          fontSize: rexTechTheme.typography.fontSize.small
        }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );

  // Password strength indicator
  const PasswordStrengthIndicator = ({ password }) => {
    if (!password) return null;
    
    const rules = checkPasswordRules(password);
    const satisfiedCount = rules.filter(rule => rule.satisfied).length;
    const totalRules = rules.length;
    const percentage = (satisfiedCount / totalRules) * 100;
    
    let strength = 'Weak';
    let color = rexTechTheme.colors.accent.orange;
    
    if (percentage >= 80) {
      strength = 'Strong';
      color = rexTechTheme.colors.primary.main;
    } else if (percentage >= 60) {
      strength = 'Good';
      color = rexTechTheme.colors.primary.light;
    } else if (percentage >= 40) {
      strength = 'Fair';
      color = rexTechTheme.colors.accent.orange;
    }
    
    return (
      <div style={{ marginTop: rexTechTheme.spacing.sm }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: rexTechTheme.spacing.xs
        }}>
          <span style={{
            fontSize: rexTechTheme.typography.fontSize.small,
            color: rexTechTheme.colors.neutral.darkGray
          }}>
            Password Strength:
          </span>
          <span style={{
            fontSize: rexTechTheme.typography.fontSize.small,
            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
            color: color
          }}>
            {strength}
          </span>
        </div>
        <div style={{
          height: '6px',
          backgroundColor: rexTechTheme.colors.neutral.gray,
          borderRadius: rexTechTheme.borderRadius.small,
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: rexTechTheme.borderRadius.small,
            transition: 'all 0.3s ease'
          }} />
        </div>
        
        {/* Password rules */}
        <div style={{ marginTop: rexTechTheme.spacing.md }}>
          <p style={{
            fontSize: rexTechTheme.typography.fontSize.small,
            color: rexTechTheme.colors.neutral.darkGray,
            marginBottom: rexTechTheme.spacing.xs
          }}>
            Password must contain:
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: rexTechTheme.spacing.xs,
            '@media (max-width: 768px)': {
              gridTemplateColumns: '1fr'
            }
          }}>
            {rules.map((rule, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  fontSize: rexTechTheme.typography.fontSize.xsmall
                }}
              >
                {rule.satisfied ? (
                  <CheckCircle size={14} color={rexTechTheme.colors.primary.main} />
                ) : (
                  <AlertCircle size={14} color={rexTechTheme.colors.neutral.gray} />
                )}
                <span style={{
                  color: rule.satisfied ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.darkGray,
                  textDecoration: rule.satisfied ? 'none' : 'line-through',
                  opacity: rule.satisfied ? 1 : 0.6
                }}>
                  {rule.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      <AdminHeader />
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: rexTechTheme.spacing.xl
      }}>
        {/* Header */}
        <div style={{
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <h1 style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.xs
              }}>
                Change Password
              </h1>
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.regular,
                margin: 0
              }}>
                Update your account password securely
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm,
              padding: rexTechTheme.spacing.md,
              backgroundColor: rexTechTheme.colors.primary.lightest,
              borderRadius: rexTechTheme.borderRadius.medium,
              border: `1px solid ${rexTechTheme.colors.primary.lighter}`
            }}>
              <Shield size={20} color={rexTechTheme.colors.primary.main} />
              <span style={{
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.medium
              }}>
                Secure Password Update
              </span>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: rexTechTheme.spacing.xl,
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr'
          }
        }}>
          {/* Left Column: Security Info */}
          <div>
            <div style={{
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.large,
              padding: rexTechTheme.spacing.xl,
              boxShadow: rexTechTheme.shadows.light,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
              textAlign: 'center',
              marginBottom: rexTechTheme.spacing.lg
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: rexTechTheme.colors.primary.lightest,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: rexTechTheme.spacing.lg,
                border: `2px solid ${rexTechTheme.colors.primary.lighter}`
              }}>
                <Lock size={40} color={rexTechTheme.colors.primary.main} />
              </div>
              
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.sm
              }}>
                Password Security
              </h3>
              
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.small,
                lineHeight: 1.6,
                marginBottom: rexTechTheme.spacing.lg
              }}>
                Keep your account secure by creating a strong, unique password that you don't use elsewhere.
              </p>
              
              <div style={{
                backgroundColor: rexTechTheme.colors.primary.lightest,
                padding: rexTechTheme.spacing.md,
                borderRadius: rexTechTheme.borderRadius.medium,
                border: `1px solid ${rexTechTheme.colors.primary.lighter}`
              }}>
                <h4 style={{
                  color: rexTechTheme.colors.primary.dark,
                  fontSize: rexTechTheme.typography.fontSize.regular,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                  marginBottom: rexTechTheme.spacing.xs
                }}>
                  Best Practices
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  textAlign: 'left'
                }}>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: rexTechTheme.spacing.xs,
                    marginBottom: rexTechTheme.spacing.xs,
                    fontSize: rexTechTheme.typography.fontSize.xsmall,
                    color: rexTechTheme.colors.neutral.darkGray
                  }}>
                    <CheckCircle size={12} color={rexTechTheme.colors.primary.main} />
                    <span>Use a combination of letters, numbers, and symbols</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: rexTechTheme.spacing.xs,
                    marginBottom: rexTechTheme.spacing.xs,
                    fontSize: rexTechTheme.typography.fontSize.xsmall,
                    color: rexTechTheme.colors.neutral.darkGray
                  }}>
                    <CheckCircle size={12} color={rexTechTheme.colors.primary.main} />
                    <span>Make it at least 12 characters long</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: rexTechTheme.spacing.xs,
                    marginBottom: rexTechTheme.spacing.xs,
                    fontSize: rexTechTheme.typography.fontSize.xsmall,
                    color: rexTechTheme.colors.neutral.darkGray
                  }}>
                    <CheckCircle size={12} color={rexTechTheme.colors.primary.main} />
                    <span>Avoid using personal information</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: rexTechTheme.spacing.xs,
                    fontSize: rexTechTheme.typography.fontSize.xsmall,
                    color: rexTechTheme.colors.neutral.darkGray
                  }}>
                    <CheckCircle size={12} color={rexTechTheme.colors.primary.main} />
                    <span>Don't reuse passwords from other sites</span>
                  </li>
                </ul>
              </div>
            </div>

            
          </div>

          {/* Right Column: Change Password Form */}
          <div>
            <div style={{
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.large,
              padding: rexTechTheme.spacing.xl,
              boxShadow: rexTechTheme.shadows.light,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`
            }}>
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Lock size={24} />
                Update Password
              </h3>
              
              {/* Status Messages */}
              {successMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.primary.lightest,
                  border: `1px solid ${rexTechTheme.colors.primary.light}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.primary.dark,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <CheckCircle size={18} />
                  {successMessage}
                </div>
              )}
              
              {errorMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.accent.lightest,
                  border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.accent.orange,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <AlertCircle size={18} />
                  {errorMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rexTechTheme.spacing.lg,
                  marginBottom: rexTechTheme.spacing.xl
                }}>
                  {/* Current Password */}
                  <PasswordInput
                    label="Current Password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    error={errors.currentPassword}
                    showPassword={showCurrentPassword}
                    onToggle={() => togglePasswordVisibility('current')}
                    onChange={handleInputChange}
                    placeholder="Enter your current password"
                  />

                  {/* New Password */}
                  <div>
                    <PasswordInput
                      label="New Password"
                      name="newPassword"
                      value={formData.newPassword}
                      error={errors.newPassword}
                      showPassword={showNewPassword}
                      onToggle={() => togglePasswordVisibility('new')}
                      onChange={handleInputChange}
                      placeholder="Create a new password"
                    />
                    <PasswordStrengthIndicator password={formData.newPassword} />
                  </div>

                  {/* Confirm Password */}
                  <PasswordInput
                    label="Confirm New Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    error={errors.confirmPassword}
                    showPassword={showConfirmPassword}
                    onToggle={() => togglePasswordVisibility('confirm')}
                    onChange={handleInputChange}
                    placeholder="Re-enter your new password"
                  />
                </div>

                {/* Submit Button */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: rexTechTheme.spacing.md,
                  paddingTop: rexTechTheme.spacing.lg,
                  borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`
                }}>
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    style={{
                      padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                      backgroundColor: rexTechTheme.colors.neutral.lightGray,
                      border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                      color: rexTechTheme.colors.neutral.darkGray,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ':hover': {
                        backgroundColor: rexTechTheme.colors.neutral.gray
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                      background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                      color: rexTechTheme.colors.neutral.white,
                      border: 'none',
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.sm,
                      opacity: isLoading ? 0.7 : 1,
                      boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                      ':hover': isLoading ? {} : {
                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}</style>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;